// SPDX-License-Identifier: AGPL-3.0-or-later

import * as Modal from '@app/features/app/components/dialogs/Modal';
import * as PollCommands from '@app/features/channel/commands/PollCommands';
import styles from '@app/features/channel/components/modals/CreatePollModal.module.css';
import {CANCEL_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import {Combobox, type ComboboxOption} from '@app/features/ui/components/form/FormCombobox';
import {Input} from '@app/features/ui/components/form/FormInput';
import {Switch} from '@app/features/ui/components/form/FormSwitch';
import {extractErrorMessage} from '@app/lib/forms';
import {
	POLL_ANSWER_MAX_LENGTH,
	POLL_ANSWERS_MAX,
	POLL_ANSWERS_MIN,
	POLL_DURATION_HOURS_DEFAULT,
	POLL_QUESTION_MAX_LENGTH,
} from '@fluxer/constants/src/PollConstants';
import type {I18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {PlusIcon, TrashIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useId, useState} from 'react';

const QUESTION_PLACEHOLDER_DESCRIPTOR = msg({
	message: 'What do you want to ask?',
	comment: 'Placeholder of the question input in the create poll dialog.',
});

const ANSWER_PLACEHOLDER_DESCRIPTOR = msg({
	message: 'Answer {number}',
	comment: 'Placeholder of an answer input in the create poll dialog. {number} is its position, starting at 1.',
});

const REMOVE_ANSWER_DESCRIPTOR = msg({
	message: 'Remove answer',
	comment: 'Accessible label of the button that removes an answer from the poll being created.',
});

const QUESTION_REQUIRED_DESCRIPTOR = msg({
	message: 'Add a question.',
	comment: 'Error shown when creating a poll without a question.',
});

const ANSWERS_REQUIRED_DESCRIPTOR = msg({
	message: 'Add at least two answers.',
	comment: 'Error shown when creating a poll with fewer than two filled-in answers.',
});

const DURATION_ARIA_DESCRIPTOR = msg({
	message: 'Duration',
	comment: 'Accessible label of the dropdown that sets how long voting stays open in the create poll dialog.',
});

type DurationUnit = 'hour' | 'day' | 'week';

const DURATIONS: ReadonlyArray<{hours: number; value: number; unit: DurationUnit}> = [
	{hours: 1, value: 1, unit: 'hour'},
	{hours: 4, value: 4, unit: 'hour'},
	{hours: 8, value: 8, unit: 'hour'},
	{hours: 24, value: 1, unit: 'day'},
	{hours: 72, value: 3, unit: 'day'},
	{hours: 168, value: 1, unit: 'week'},
	{hours: 336, value: 2, unit: 'week'},
];

function buildDurationOptions(i18n: I18n): Array<ComboboxOption<number>> {
	return DURATIONS.map(({hours, value, unit}) => ({
		value: hours,
		label: new Intl.NumberFormat(i18n.locale, {style: 'unit', unit, unitDisplay: 'long'}).format(value),
	}));
}

interface CreatePollModalProps {
	channelId: string;
}

const CreatePollModal: React.FC<CreatePollModalProps> = observer(({channelId}) => {
	const {i18n} = useLingui();
	const questionId = useId();
	const [question, setQuestion] = useState('');
	const [answers, setAnswers] = useState<Array<string>>(['', '']);
	const [durationHours, setDurationHours] = useState(POLL_DURATION_HOURS_DEFAULT);
	const [allowMultiselect, setAllowMultiselect] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const setAnswer = (index: number, value: string) =>
		setAnswers((current) => current.map((answer, answerIndex) => (answerIndex === index ? value : answer)));

	const handleSubmit = async (event: React.FormEvent) => {
		event.preventDefault();
		const trimmedQuestion = question.trim();
		const filledAnswers = answers.map((answer) => answer.trim()).filter(Boolean);
		if (!trimmedQuestion) {
			setError(i18n._(QUESTION_REQUIRED_DESCRIPTOR));
			return;
		}
		if (filledAnswers.length < POLL_ANSWERS_MIN) {
			setError(i18n._(ANSWERS_REQUIRED_DESCRIPTOR));
			return;
		}
		try {
			setSubmitting(true);
			setError(null);
			await PollCommands.createPoll(channelId, {
				question: trimmedQuestion,
				answers: filledAnswers,
				allow_multiselect: allowMultiselect,
				duration_hours: durationHours,
			});
			ModalCommands.pop();
		} catch (createError) {
			setError(extractErrorMessage(i18n, createError));
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Modal.Root size="small" centered data-flx="channel.create-poll-modal.modal-root">
			<form onSubmit={handleSubmit} data-flx="channel.create-poll-modal.form.submit">
				<Modal.Header title={<Trans>Create poll</Trans>} data-flx="channel.create-poll-modal.modal-header" />
				<Modal.Content data-flx="channel.create-poll-modal.modal-content">
					<div className={styles.form} data-flx="channel.create-poll-modal.form">
						<div className={styles.fieldGroup} data-flx="channel.create-poll-modal.field-group">
							<label htmlFor={questionId} className={styles.label} data-flx="channel.create-poll-modal.label">
								<Trans>Question</Trans>
							</label>
							<Input
								id={questionId}
								value={question}
								maxLength={POLL_QUESTION_MAX_LENGTH}
								onChange={(event) => setQuestion(event.target.value)}
								placeholder={i18n._(QUESTION_PLACEHOLDER_DESCRIPTOR)}
								disabled={submitting}
								data-flx="channel.create-poll-modal.input.set-question"
							/>
						</div>

						<div className={styles.fieldGroup} data-flx="channel.create-poll-modal.field-group--2">
							<span className={styles.label} data-flx="channel.create-poll-modal.label--2">
								<Trans>Answers</Trans>
							</span>
							{answers.map((answer, index) => (
								<div key={index} className={styles.answerRow} data-flx="channel.create-poll-modal.answer-row">
									<Input
										value={answer}
										maxLength={POLL_ANSWER_MAX_LENGTH}
										onChange={(event) => setAnswer(index, event.target.value)}
										placeholder={i18n._(ANSWER_PLACEHOLDER_DESCRIPTOR, {number: index + 1})}
										disabled={submitting}
										data-flx="channel.create-poll-modal.input.set-answer"
									/>
									{answers.length > POLL_ANSWERS_MIN && (
										<Button
											variant="secondary"
											compact
											type="button"
											aria-label={i18n._(REMOVE_ANSWER_DESCRIPTOR)}
											leftIcon={<TrashIcon size={16} data-flx="channel.create-poll-modal.trash-icon" />}
											onClick={() => setAnswers((current) => current.filter((_, answerIndex) => answerIndex !== index))}
											disabled={submitting}
											data-flx="channel.create-poll-modal.button.set-answers"
										/>
									)}
								</div>
							))}
							{answers.length < POLL_ANSWERS_MAX && (
								<div data-flx="channel.create-poll-modal.div">
									<Button
										variant="secondary"
										compact
										type="button"
										leftIcon={<PlusIcon size={16} data-flx="channel.create-poll-modal.plus-icon" />}
										onClick={() => setAnswers((current) => [...current, ''])}
										disabled={submitting}
										data-flx="channel.create-poll-modal.button.set-answers--2"
									>
										<Trans>Add answer</Trans>
									</Button>
								</div>
							)}
						</div>

						<div className={styles.fieldGroup} data-flx="channel.create-poll-modal.field-group--3">
							<span className={styles.label} data-flx="channel.create-poll-modal.label--3">
								<Trans>Duration</Trans>
							</span>
							<Combobox
								value={durationHours}
								options={buildDurationOptions(i18n)}
								onChange={setDurationHours}
								aria-label={i18n._(DURATION_ARIA_DESCRIPTOR)}
								disabled={submitting}
								data-flx="channel.create-poll-modal.combobox.set-duration-hours"
							/>
						</div>

						<Switch
							label={<Trans>Allow multiple answers</Trans>}
							value={allowMultiselect}
							onChange={setAllowMultiselect}
							disabled={submitting}
							data-flx="channel.create-poll-modal.switch.set-allow-multiselect"
						/>

						{error && (
							<p className={styles.error} role="alert" data-flx="channel.create-poll-modal.error">
								{error}
							</p>
						)}
					</div>
				</Modal.Content>
				<Modal.Footer data-flx="channel.create-poll-modal.modal-footer">
					<Button
						variant="secondary"
						type="button"
						onClick={() => ModalCommands.pop()}
						disabled={submitting}
						data-flx="channel.create-poll-modal.button.pop"
					>
						{i18n._(CANCEL_DESCRIPTOR)}
					</Button>
					<Button
						variant="primary"
						type="submit"
						submitting={submitting}
						data-flx="channel.create-poll-modal.button.submit"
					>
						<Trans>Create poll</Trans>
					</Button>
				</Modal.Footer>
			</form>
		</Modal.Root>
	);
});

export function openCreatePollModal(channelId: string): void {
	ModalCommands.push(
		modal(() => (
			<CreatePollModal
				channelId={channelId}
				data-flx="channel.create-poll-modal.open-create-poll-modal.create-poll-modal"
			/>
		)),
	);
}
